import React from "react";
import EsNavbar from "@/components/EsNavbar";
import EsFooter from "@/components/sections/EsFooter";
import Consultation from "@/components/sections/Consultation";
import {
  CLINIC_REF,
  SITE_URL,
  buildBreadcrumbList,
  Crumb,
} from "@/components/schemaConstants";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import { CheckCircle2, Zap, Brain, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ClinicalReviewNote from "./ClinicalReviewNote";

export interface EsConditionLink {
  title: string;
  href: string;
}

/** Spanish sidebar nav for the ketamine condition pages (all /es/*). */
export const esConditionNavLinks: EsConditionLink[] = [
  { title: "Ansiedad", href: "/es/anxiety-treatment-coral-springs-fl" },
  {
    title: "Trastorno bipolar",
    href: "/es/bipolar-disorder-coral-springs-fl",
  },
  {
    title: "Dolor crónico",
    href: "/es/chronic-pain-coral-springs-fl",
  },
  {
    title: "Depresión",
    href: "/es/depression-treatment-coral-springs-fl",
  },
  { title: "SDRC (CRPS)", href: "/es/crps-treatment-coral-springs-fl" },
  {
    title: "Fibromialgia",
    href: "/es/fibromyalgia-treatment-coral-springs-fl",
  },
  { title: "Migrañas", href: "/es/migraines-treatment-coral-springs-fl" },
  {
    title: "Dolor neuropático",
    href: "/es/neuropathic-pain-treatment-coral-springs-fl",
  },
  { title: "TOC", href: "/es/ocd-treatment-coral-springs-fl" },
  { title: "TEPT", href: "/es/ptsd-treatment-coral-springs-fl" },
  {
    title: "Ideación suicida",
    href: "/es/suicidal-ideation-treatment-coral-springs-fl",
  },
];

export interface EsConditionSchemaInput {
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  breadcrumbs?: Crumb[];
  extraSchema?: object | object[];
}

/**
 * Build the page-level JSON-LD graphs for a Spanish condition page.
 * Reuses the locale-agnostic MedicalWebPage + BreadcrumbList builder.
 */
export function buildEsConditionSchema(
  input: EsConditionSchemaInput,
): object[] {
  const { seoTitle, seoDescription, canonical, breadcrumbs, extraSchema } =
    input;
  const canonicalPath = canonical.replace(/\/+$/, "") || "/";
  const url = `${SITE_URL.replace(/\/$/, "")}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`;
  const blocks: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: seoTitle.split("|")[0].trim(),
      description: seoDescription,
      url,
      provider: CLINIC_REF,
      inLanguage: "es",
    },
  ];
  if (breadcrumbs && breadcrumbs.length > 0) {
    blocks.push(buildBreadcrumbList(breadcrumbs, canonicalPath));
  }
  if (extraSchema) {
    const extra = Array.isArray(extraSchema) ? extraSchema : [extraSchema];
    blocks.push(...extra);
  }
  return blocks;
}

export interface EsConditionConfig {
  canonical: string;
  badge: string;
  title: string;
  subtitle: string;
  introHeading: string;
  introText: string[];
  whatIsTitle?: string;
  whatIsText?: string[];
  howItHelpsTitle?: string;
  howItHelpsText?: string[];
  treatmentExperienceTitle?: string;
  treatmentExperienceText?: string[];
  benefitsTitle?: string;
  benefits?: Array<{ title: string; desc: string }>;
  extraSections?: Array<{ title: string; content: string[] }>;
  imageUrl?: string;
  imageAlt?: string;
  activeConditionHref: string;
  seoTitle: string;
  seoDescription: string;
  keywords?: string;
  reviewedBy?: string;
  lastUpdated?: string;
  breadcrumbs?: Crumb[];
}

const PHONE = "954-827-9793";

/**
 * Spanish-language condition treatment page layout. Mirrors the English
 * ConditionPageLayout with Spanish chrome (EsNavbar / EsFooter), Spanish
 * strings, and a Spanish sidebar nav that links to /es/* condition pages.
 */
export const ConditionPageLayoutEs: React.FC<{
  cfg: EsConditionConfig;
}> = ({ cfg }) => {
  return (
    <div className="min-h-screen bg-background">
      <EsNavbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="relative text-primary-foreground py-16 sm:py-24 overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
          <div className="container-px relative mx-auto max-w-5xl px-4">
            <div className="max-w-xl text-left space-y-5">
              <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-6 py-2 text-sm sm:text-base font-bold text-white shadow-lg uppercase tracking-wider">
                {cfg.badge}
              </span>
              <h1 className="font-headings text-3xl sm:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
                {cfg.title}
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
                {cfg.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
                <a
                  href="/es/saint"
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-accent px-8 py-6 text-base font-bold text-accent-foreground shadow-glow hover:opacity-90 transition-opacity"
                >
                  Solicitar una consulta
                </a>
                <a
                  href={`tel:${PHONE.replace(/-/g, "")}`}
                  className="inline-flex items-center justify-center rounded-xl border-2 border-white/60 bg-white/10 backdrop-blur-sm px-8 py-6 text-base font-bold text-white hover:bg-white/20 hover:border-white/80 transition-all"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Llamar {PHONE}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumbs */}
        {cfg.breadcrumbs && cfg.breadcrumbs.length > 0 && (
          <div className="bg-background py-3">
            <div className="container-px mx-auto max-w-6xl px-4">
              <Breadcrumbs crumbs={cfg.breadcrumbs} />
            </div>
          </div>
        )}

        {/* Content + Sidebar */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              {(cfg.reviewedBy || cfg.lastUpdated) && (
                <ClinicalReviewNote
                  reviewedBy={cfg.reviewedBy}
                  lastUpdated={cfg.lastUpdated}
                />
              )}
              <div className="space-y-4">
                <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                  {cfg.introHeading}
                </h2>
                {cfg.introText.map((p, i) => (
                  <p
                    key={i}
                    className="text-foreground/85 text-base sm:text-lg leading-relaxed"
                  >
                    {p}
                  </p>
                ))}
              </div>

              {cfg.whatIsTitle && cfg.whatIsText && (
                <div className="rounded-2xl bg-card border border-border p-6 sm:p-8 shadow-soft space-y-4">
                  <div className="flex items-center gap-3 text-primary">
                    <Brain className="h-6 w-6 text-accent shrink-0" />
                    <h3 className="font-headings text-xl font-bold">
                      {cfg.whatIsTitle}
                    </h3>
                  </div>
                  {cfg.whatIsText.map((p, i) => (
                    <p
                      key={i}
                      className="text-foreground/85 text-sm sm:text-base leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {cfg.howItHelpsTitle && cfg.howItHelpsText && (
                <div className="space-y-4">
                  <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                    <Zap className="h-6 w-6 text-accent shrink-0" />
                    {cfg.howItHelpsTitle}
                  </h3>
                  {cfg.howItHelpsText.map((p, i) => (
                    <p
                      key={i}
                      className="text-foreground/85 text-base leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {cfg.treatmentExperienceText && (
                <div className="rounded-2xl bg-light-blue/30 border border-light-blue p-6 sm:p-8 space-y-4">
                  <h3 className="font-headings text-xl font-bold text-primary">
                    {cfg.treatmentExperienceTitle}
                  </h3>
                  {cfg.treatmentExperienceText.map((p, i) => (
                    <p
                      key={i}
                      className="text-foreground/85 text-base leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {cfg.imageUrl && (
                <div className="rounded-2xl overflow-hidden border border-border shadow-soft">
                  <img
                    src={cfg.imageUrl}
                    alt={cfg.imageAlt || cfg.title}
                    className="w-full h-auto object-cover max-h-96"
                    loading="lazy"
                    width={1200}
                    height={675}
                  />
                </div>
              )}

              {cfg.benefits && cfg.benefits.length > 0 && (
                <div className="space-y-6">
                  <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                    {cfg.benefitsTitle}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {cfg.benefits.map((b, i) => (
                      <div
                        key={i}
                        className="rounded-xl bg-card border border-border p-5 shadow-soft space-y-2"
                      >
                        <div className="flex items-center gap-2 text-accent font-bold text-base">
                          <CheckCircle2 className="h-5 w-5 shrink-0" />
                          <span>{b.title}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed">
                          {b.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cfg.extraSections &&
                cfg.extraSections.map((sec, i) => (
                  <div key={i} className="space-y-3 pt-2">
                    <h3 className="font-headings text-xl font-bold text-primary">
                      {sec.title}
                    </h3>
                    {sec.content.map((p, j) => (
                      <p
                        key={j}
                        className="text-foreground/85 text-base leading-relaxed"
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                ))}

              <div className="rounded-2xl bg-gradient-navy text-white p-8 text-center space-y-4 shadow-premium">
                <h3 className="font-headings text-2xl font-bold">
                  ¿Listo para comenzar su camino hacia la recuperación?
                </h3>
                <p className="text-primary-foreground/80 max-w-xl mx-auto text-sm sm:text-base">
                  Contáctese hoy con Neuro Wellness TMS Centers of America en
                  Coral Springs, FL para programar una consulta confidencial.
                </p>
                <div className="pt-2 flex justify-center gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                  >
                    <a href={`tel:${PHONE.replace(/-/g, "")}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      Llamar (954) 827-9793
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4 sticky top-24">
                <h3 className="font-headings text-xl font-bold text-primary text-center">
                  Solicitar una cita
                </h3>
                <p className="text-xs text-muted-foreground text-center">
                  Complete el formulario a continuación o llámenos al (954)
                  827-9793.
                </p>
                <a
                  href="/es/saint"
                  className="block w-full py-3 px-4 rounded-xl bg-gradient-accent text-accent-foreground text-center font-bold text-sm shadow-glow hover:opacity-90 transition-all"
                >
                  Consulta gratuita
                </a>

                <hr className="border-border" />

                <div className="space-y-3">
                  <h4 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    Afecciones tratadas con ketamina
                  </h4>
                  <div className="flex flex-col gap-1.5">
                    {esConditionNavLinks.map((item, idx) => {
                      const isActive = item.href === cfg.activeConditionHref;
                      return (
                        <Link
                          key={idx}
                          to={item.href}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-between ${
                            isActive
                              ? "bg-accent text-white shadow-sm"
                              : "bg-muted/40 text-foreground/85 hover:bg-muted hover:text-primary"
                          }`}
                        >
                          <span>{item.title}</span>
                          <ArrowRight
                            className={`h-3.5 w-3.5 ${isActive ? "text-white" : "opacity-40"}`}
                          />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Consultation />
      </main>
      <EsFooter />
    </div>
  );
};
